/* MIS MarineBond reviews carousel: native scroll-snap, dots, prev/next, "Read more". No autoplay. */
if (!customElements.get('mb-carousel')) {
  class MbCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-mb-track]');
      this.slides = Array.from(this.querySelectorAll('[data-mb-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-mb-dot]'));
      this.prev = this.querySelector('[data-mb-prev]');
      this.next = this.querySelector('[data-mb-next]');
      if (!this.track || this.slides.length === 0) return;

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });
      // Prev/Next use aria-disabled (not the disabled attribute), so a focused button keeps keyboard focus at the ends.
      if (this.prev)
        this.prev.addEventListener('click', () => {
          if (this.prev.getAttribute('aria-disabled') === 'true') return;
          this.goTo(this.current() - this.perView());
        });
      if (this.next)
        this.next.addEventListener('click', () => {
          if (this.next.getAttribute('aria-disabled') === 'true') return;
          this.goTo(this.current() + this.perView());
        });

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.moreButtons = Array.from(this.querySelectorAll('[data-mb-more]'));
      this.moreButtons.forEach((button) => {
        const quote = document.getElementById(button.getAttribute('aria-controls'));
        if (!quote) return;
        button.addEventListener('click', () => {
          const open = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!open));
          quote.classList.toggle('is-open', !open);
          button.textContent = open ? button.dataset.more : button.dataset.less;
        });
      });
      this.updateMore = this.updateMore.bind(this);
      this.updateMore();
      window.addEventListener('load', this.updateMore);
      window.addEventListener('resize', this.updateMore, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.updateMore);

      this.onScroll();
    }

    updateMore() {
      (this.moreButtons || []).forEach((button) => {
        const quote = document.getElementById(button.getAttribute('aria-controls'));
        if (!quote || button.getAttribute('aria-expanded') === 'true') return;
        const clamped = quote.getBoundingClientRect().height;
        quote.classList.add('is-open');
        const full = quote.getBoundingClientRect().height;
        quote.classList.remove('is-open');
        button.hidden = full <= clamped + 2;
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.updateMore);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    perView() {
      const slideWidth = this.slides[0].getBoundingClientRect().width || 1;
      return Math.max(1, Math.round(this.track.clientWidth / slideWidth));
    }

    current() {
      const slideWidth = this.slides[0].getBoundingClientRect().width || 1;
      return Math.round(this.track.scrollLeft / slideWidth);
    }

    goTo(index) {
      const max = Math.max(0, this.slides.length - this.perView());
      const target = Math.min(Math.max(index, 0), max);
      const left = this.slides[target].offsetLeft - this.slides[0].offsetLeft;
      this.track.scrollTo({ left: left, behavior: this.reduceMotion ? 'auto' : 'smooth' });
    }

    onScroll() {
      const perView = this.perView();
      const max = Math.max(0, this.slides.length - perView);
      const current = Math.min(this.current(), max);
      this.dots.forEach((dot, index) => {
        const active = index === current;
        dot.setAttribute('aria-current', active ? 'true' : 'false');
        dot.hidden = index > max;
      });
      if (this.prev) this.prev.setAttribute('aria-disabled', String(current <= 0));
      if (this.next) this.next.setAttribute('aria-disabled', String(current >= max));
      const nav = this.querySelector('[data-mb-nav]');
      if (nav) nav.hidden = max === 0;
    }
  }
  customElements.define('mb-carousel', MbCarousel);
}

/* "How It Works" video tile. Nothing loads or autoplays until the visitor clicks play.
   The click is the user gesture, so the YouTube embed starts with sound on (autoplay=1, mute=0),
   with normal controls and fullscreen. No YouTube API script is loaded. */
if (!customElements.get('mb-video')) {
  class MbVideo extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      if (!button) return;
      button.addEventListener('click', () => this.play(), { once: true });
    }

    play() {
      const id = this.dataset.videoId;
      if (!id) return;
      const params = new URLSearchParams({
        autoplay: '1',
        mute: '0',
        controls: '1',
        fs: '1',
        playsinline: '1',
        rel: '0',
      });
      const iframe = document.createElement('iframe');
      iframe.src = 'https://www.youtube.com/embed/' + encodeURIComponent(id) + '?' + params.toString();
      iframe.title = this.dataset.title || 'Video';
      iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media; picture-in-picture');
      this.innerHTML = '';
      this.appendChild(iframe);
      iframe.focus();
    }
  }
  customElements.define('mb-video', MbVideo);
}
